import React, { useCallback, useEffect, useState } from 'react';
import SoundOff from '@uber/base-icons/sound-off';
import Sound from '@uber/base-icons/sound-outlined';
import { useStyletron } from 'baseui';
import { PLACEMENT, StatefulPopover, TRIGGER_TYPE } from 'baseui/popover';
import { StatefulTooltip } from 'baseui/tooltip';
import useShortcut from '../../image/labelling/providers/hooks/use-shortcut';
import { handleShortcutRegistrationError } from '../../image/labelling/providers/utils/shortcut';
import { VOLUME_MULTIPLIER, VOLUME_STEP } from './constants';
import { IconButton } from './icon-button';
import type { VolumeSliderProps } from './types';
import { VerticalSlider } from './vertical-slider';
export const VolumeSlider = ({
  volume: volumeProp = 1,
  onVolumeChange,
  onVolumeFinalChange,
  iconSize = 16,
  buttonOverrides
}: VolumeSliderProps) => {
  const [css, theme] = useStyletron();
  const [volume, setVolume] = useState(volumeProp * VOLUME_MULTIPLIER);
  const {
    registerHotKeys
  } = useShortcut();
  const handleChange = useCallback((value: number) => {
    setVolume(value);
    onVolumeChange(value / VOLUME_MULTIPLIER);
  }, [onVolumeChange]);
  const handleFinalChange = useCallback((value: number) => {
    onVolumeFinalChange?.(value / VOLUME_MULTIPLIER);
  }, [onVolumeFinalChange]);
  useEffect(() => {
    const {
      errors,
      removeHotKeys
    } = registerHotKeys([{
      keys: ['Shift', 'ArrowUp'],
      onAction: () => handleChange(Math.min(volume + VOLUME_STEP, VOLUME_MULTIPLIER))
    }, {
      keys: ['Shift', 'ArrowDown'],
      onAction: () => handleChange(Math.max(volume - VOLUME_STEP, 0))
    }]);
    errors.forEach(error => handleShortcutRegistrationError(error));
    return () => {
      removeHotKeys();
    };
  }, [registerHotKeys, volume, handleChange]);
  const isMuted = volume <= 0;
  const tooltip = isMuted ? 'Volume: Muted' : `Volume: ${volume}%`;
  return <StatefulTooltip content={tooltip} placement="bottom" showArrow>
      <StatefulPopover placement={PLACEMENT.top} triggerType={TRIGGER_TYPE.click} overrides={{
      Body: {
        style: {
          zIndex: 5
        }
      }
    }} content={<div className={css({
      height: theme.sizing.scale3200,
      width: theme.sizing.scale950,
      background: theme.colors.backgroundSecondary,
      borderRadius: theme.borders.radius300,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: theme.sizing.scale100
    })}>
            <VerticalSlider min={0} max={VOLUME_MULTIPLIER} value={volume} onChange={handleChange} onFinalChange={handleFinalChange} />
          </div>} accessibilityType="tooltip">
        <IconButton overrides={buttonOverrides}>
          {isMuted ? <SoundOff size={iconSize} title={tooltip} /> : <Sound size={iconSize} title={tooltip} />}
        </IconButton>
      </StatefulPopover>
    </StatefulTooltip>;
};